import {
  useCallback,
  useEffect,
  lazy,
  Suspense,
  useState,
  createContext,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { userActions } from "store-redux/user-slice";

import {
  API_BASE_URL,
  APP_PREFIX_PATH,
  GOOGLE_MAP_API_KEY,
} from "configs/AppConfig";

import Layout from "components/layout/Layout";
import LoadingSpinner from "components/UI/loading-spinner/LoadingSpinner";
import SignUp from "pages/Signup";
import { ConfigProvider } from "antd";
import PackageDetail from "components/page-content/package-detail-content/PackageDetail";
import { filterActions } from "store-redux/filter-slice";
import SearchPage from "components/page-content/search-content/SearchPageNew";
import { cartActions } from "store-redux/cart-slice";
import { wishlistActions } from "store-redux/wishlist-slice";
import { USER_FILTER_VALUES } from "helpers/constants";
import { widgetsActions } from "store-redux/widgets-slice";
import { settingsActions } from "store-redux/settings-slice";
import { pincodefetch } from "hooks/pincodeFetch";
import SessionModalManager from "components/page-content/home-content/initial-popup/SessionModalManager";
import VendorsList from "components/page-content/vendor-page/VendorsList";
import VendorDetail from "components/page-content/vendor-page/vendor-detail/VendorDetail";

//Define the main pages urls here
// 🟢 Eager load Home
import Home from "pages/Home";
import ListingByCategoryAndLocation from "pages/ListingByCategoryAndLocation";
import SearchPageLatest from "components/page-content/search-latesst/search-page/SearchPage";

// 🔁 Lazy load all other route components
const FAQ = lazy(() => import("pages/FAQ"));
const PrivacyPolicy = lazy(() => import("pages/PrivacyPolicy"));
const AboutUsTemplate = lazy(() => import("components/page-content/informations/AboutUsTemplate"));
const PlanWithUs = lazy(() => import("components/page-content/plan-with-us/PlanWithUs"));
const PackageSearch = lazy(() => import("components/page-content/package-search/PackageSearch"));
const WishList = lazy(() => import("components/page-content/WIshList/WishList"));
const BookingsPage = lazy(() => import("components/page-content/BookingsPage/BookingsPage"));
const ProfileSettings = lazy(() => import("components/page-content/ProfileSettings/ProfileSettings"));
const ContactUs = lazy(() => import("components/page-content/informations/contact-us/ContactUs"));
const Checkout = lazy(() => import("components/page-content/Checkout/Checkout"));
const PolicyDetail = lazy(() => import("components/page-content/policy-content/PolicyDetail"));
const BlogsList = lazy(() => import("components/page-content/blogs-page/blogs-list/BlogsList"));
const BlogDetail = lazy(() => import("components/page-content/blogs-page/blog-detail/Blogdetail"));
const Cart = lazy(() => import("pages/Cart"));
const VenueDetail = lazy(() => import("components/page-content/venue-content/VenueDetail"));
const Gallery = lazy(() => import("components/page-content/gallery-content/Gallery"));
const PaymentSuccess = lazy(() => import("components/payment-success/PaymentSuccess"));
const PaymentFailure = lazy(() => import("components/payment-failed/PaymentFailed"));
const NotFound = lazy(() => import("components/not-found/NotFound"));
const Login = lazy(() => import("pages/Login"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 2000,
    },
  },
});

export const NavigationContext = createContext();

const App = () => {
  // access the modalSlice State in the Store Reducer
  const { modalIsVisible, modalType } = useSelector((state) => state.modal);
  const { user, isLoggedIn } = useSelector((state) => state.user);
  const [checkForAuthorization, setCheckForAuthorization] = useState(false);
  const [locationValue, setLocationValue] = useState({});
  const [constants, setConstants] = useState({});
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const [searchPageLoading, setSearchPageLoading] = useState(false);
  const { homeApis } = useSelector((state) => state.widgets); // Access widget data

  const dispatch = useDispatch();

  const location = useLocation();

  const [headerScript, setHeaderScript] = useState("");
  const [footerScript, setFooterScript] = useState("");

  if (process.env.NODE_ENV === "production") {
    console.log = () => {};
    console.warn = () => {};
    console.error = () => {};
  }

  useEffect(() => {
    setHeaderScript(homeApis?.settings?.[0]?.headscript);
    setFooterScript(homeApis?.settings?.[0]?.bodyscript);
    dispatch(
      settingsActions.toggleSettings({
        settings: homeApis?.settings?.[0],
      })
    );
    // getSettings();
  }, [homeApis]);

  // remove pagination local storage when previous url is not search

  // Track last two paths
  const prevPathRef = useRef("");
  const beforePrevPathRef = useRef("");

  useEffect(() => {
    const currentPath = location.pathname;

    const cameFromSearch = beforePrevPathRef.current.includes("/search");

    const isNowSearchOrVenue =
      currentPath.includes("/search") || isVenueDetailPath(currentPath);
    const wasJustSearch = prevPathRef.current.includes("/search");

    // if (cameFromSearch && !isNowSearchOrVenue && !wasJustSearch) {
    if (!isNowSearchOrVenue) {
      localStorage.removeItem("search-page-pagination");
    }

    beforePrevPathRef.current = prevPathRef.current;
    prevPathRef.current = currentPath;
  }, [location]);

  useEffect(() => {
    const currentPath = location.pathname;
    const isNowSearchOrVenue = currentPath.includes("/blog");

    // if (cameFromSearch && !isNowSearchOrVenue && !wasJustSearch) {
    if (!isNowSearchOrVenue) {
      localStorage.removeItem("blogs-page-pagination");
    }
  }, [location]);

  const isVenueDetailPath = (path) => {
    // Matches something like /weddings/venue-abc or /corporate/venue-xyz
    const venueDetailPattern = /^\/[^/]+\/[^/]+$/;
    return venueDetailPattern.test(path);
  };

  console.log("head and body", headerScript, footerScript);

  useEffect(() => {
    // dispatch(widgetsActions.setWidgets({ widgets: widgett }));
    const fetchData = async () => {
      try {
        // const response = await fetch(`${API_BASE_URL}home/generate-file`);

        // const data = await response.json();
        // const fileUrl = data.fileUrl;

        const fileUrl = constants?.widgetTxt;

        // Fetch the text file
        const textResponse = await fetch(fileUrl, {
          method: "GET",
          headers: {
            "Cache-Control": "no-cache",
          },
          cache: "no-cache",
        });
        console.log("text response is", textResponse);

        const textData = await textResponse.text();
        console.log("text data is", textData);
        const parsedData = JSON.parse(textData);
        console.log("parsed data", parsedData);
        const widgets = parsedData?.widgets;
        dispatch(widgetsActions.setWidgets({ widgets: parsedData?.widgets }));
        console.log("widgets in app.js", widgets);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (constants?.widgetTxt) {
      fetchData();
    }
  }, [constants]);

  useEffect(() => {
    const fetchHomeApi = async () => {
      try {
        const fileUrl = constants?.generalTxt;

        const textResponse = await fetch(fileUrl, {
          method: "GET",
          headers: {
            "Cache-Control": "no-cache",
          },
          cache: "no-cache",
        });
        console.log("text response is", textResponse);

        const textData = await textResponse.text();
        console.log("text data is", textData);
        const parsedData = JSON.parse(textData);
        console.log("parsed data home file", parsedData);
        const homeApis = parsedData;
        console.log("data in app.js", homeApis);
        dispatch(widgetsActions.setApis({ homeApis: parsedData }));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (constants?.generalTxt) {
      fetchHomeApi();
    }
  }, [constants]);

  const getTextFiles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}home/getTxt/public`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      console.log("fetched data", data);
      if (data) {
        setConstants(data?.data);
      } else {
      }
    } catch (err) {}
  };

  useEffect(() => {
    getTextFiles();
  }, []);

  // useEffect(() => {
  //   if (headerScript) {
  //     const script = document.createElement("script");
  //     script.innerHTML = headerScript;
  //     document.head.appendChild(script);
  //   }
  // }, [headerScript]);

  // useEffect(() => {
  //   if (footerScript) {
  //     const script = document.createElement("script");
  //     script.innerHTML = footerScript;
  //     document.body.appendChild(script);
  //   }
  // }, [footerScript]);

  useEffect(() => {
    if (!headerScript) return;

    // Use a temporary container to parse all tags (including <noscript>)
    const container = document.createElement("div");
    container.innerHTML = headerScript;

    const appendedScripts = [];
    const appendedNoscripts = [];

    // Process script tags
    const scriptElements = container.querySelectorAll("script");
    scriptElements.forEach((scriptElement) => {
      const script = document.createElement("script");
      script.type = scriptElement.getAttribute("type") || "text/javascript";

      if (scriptElement.src) {
        script.src = scriptElement.src;
        script.async = true;
      } else {
        script.textContent = scriptElement.textContent;
      }

      if (scriptElement.hasAttribute("defer")) {
        script.defer = true;
      }

      if (scriptElement.hasAttribute("referrerpolicy")) {
        script.referrerPolicy = scriptElement.getAttribute("referrerpolicy");
      }

      document.head.appendChild(script);
      appendedScripts.push(script);
    });

    // Process noscript tags (preserved now)
    // const noscriptElements = container.querySelectorAll("noscript");
    // noscriptElements.forEach((noscriptEl) => {
    //   const wrapper = document.createElement("div");
    //   wrapper.innerHTML = noscriptEl.innerHTML;
    //   //wrapper.style.display = "none"; // optional: visually hidden
    //   document.body.appendChild(wrapper);
    //   appendedNoscripts.push(wrapper);
    // });

    // Cleanup
    return () => {
      appendedScripts.forEach((script) => {
        document.head.removeChild(script);
      });
      // appendedNoscripts.forEach((el) => {
      //   document.body.removeChild(el);
      // });
    };
  }, [headerScript]);

  useEffect(() => {
    if (!footerScript) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(footerScript, "text/html");

    // Process <script> tags
    const scriptElements = doc.querySelectorAll("script");
    const appendedScripts = [];

    scriptElements.forEach((scriptElement) => {
      const script = document.createElement("script");
      script.type = scriptElement.getAttribute("type") || "text/javascript";

      if (scriptElement.src) {
        script.src = scriptElement.src;
        script.async = true;
      } else {
        script.textContent = scriptElement.textContent;
      }

      if (scriptElement.hasAttribute("defer")) {
        script.defer = true;
      }

      if (scriptElement.hasAttribute("referrerpolicy")) {
        script.referrerPolicy = scriptElement.getAttribute("referrerpolicy");
      }

      document.body.appendChild(script);
      appendedScripts.push(script);
    });

    // Process <noscript> (for preserving in DOM, optional)
    // const noscriptElements = doc.querySelectorAll("noscript");
    // const appendedNoscripts = [];

    // noscriptElements.forEach((noscriptEl) => {
    //   const wrapper = document.createElement("div");
    //   wrapper.innerHTML = noscriptEl.innerHTML; // not actual <noscript>, just a div
    //   wrapper.style.display = "none"; // hide it, just in case
    //   document.body.appendChild(wrapper);
    //   appendedNoscripts.push(wrapper);
    // });

    return () => {
      appendedScripts.forEach((script) => {
        document?.head?.removeChild(script);
      });
      // appendedNoscripts.forEach((el) => {
      //   document.body.removeChild(el);
      // });
    };
  }, [footerScript]);

  //--------Use this code if there is login in the project----------------
  const autoLoginHandler = useCallback(() => {
    dispatch(userActions.autoLogin());
  }, [dispatch]);

  useEffect(() => {
    // auto login first time app is running
    autoLoginHandler();
    setCheckForAuthorization(true);
  }, [autoLoginHandler]);

  const grantLoggedInAccess = checkForAuthorization && isLoggedIn;
  const preventAccess = checkForAuthorization && !isLoggedIn;
  //----------------------------------------------------------------------

  const autoLocationHandler = useCallback(() => {
    dispatch(filterActions.autoFillLocation());
  }, [dispatch]);

  // const getCurrentLocation = () => {
  //   if (navigator.geolocation) {
  //     navigator.geolocation.getCurrentPosition(async (position) => {
  //       const { latitude, longitude } = position.coords;
  //       console.log("Lat:", latitude, "Lng:", longitude);

  //       const apiKey = "YOUR_GOOGLE_API_KEY"; // must have Geocoding API enabled
  //       const response = await fetch(
  //         `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAP_API_KEY}`
  //       );
  //       const data = await response.json();

  //       if (data.results[0]) {
  //         console.log("Address:", data.results[0].formatted_address);
  //       }
  //     });
  //   } else {
  //     console.error("Geolocation not supported");
  //   }
  // };

  // useEffect(() => {
  //   getCurrentLocation();
  // }, []);

  const getAccurateLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          console.log("Lat:", latitude, "Lon:", longitude);
          console.log("Accuracy:", accuracy, "meters");

          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await res.json();
            console.log("final rrt", data);
            console.log(
              "City:",
              data.address.city || data.address.town || data.address.village
            );
            console.log("State:", data.address.state);
            console.log("Country:", data.address.country);

            // pincode fetch
            const pincode = await pincodefetch(latitude, longitude);

            dispatch(
              filterActions.togglelocation({
                latitude,
                longitude,
                accuracy,
                address: `${data?.address?.county}, ${data?.address?.state}, ${data?.address?.country}`,
                place: data?.address?.county,
                pincode: pincode,
              })
            );
          } catch (err) {
            console.error("Failed to reverse geocode:", err);
            dispatch(
              filterActions.togglelocation({
                latitude: 9.9312328,
                longitude: 76.26730409999999,
                accuracy: null,
                address: `Kochi, Kerala, India`,
                place: "Kochi",
              })
            );
          }
        },
        (error) => {
          console.error("Geolocation error:", error.message);
          dispatch(
            filterActions.togglelocation({
              latitude: 9.9312328,
              longitude: 76.26730409999999,
              accuracy: null,
              address: `Kochi, Kerala, India`,
              place: "Kochi",
              pincode: "682001",
            })
          );
        },
        { enableHighAccuracy: true }
      );
    } else {
      console.error("Geolocation not supported");
    }
  };

  useEffect(() => {
    const storedLocation = JSON.parse(localStorage.getItem(USER_FILTER_VALUES));
    if (!storedLocation || Object.keys(storedLocation).length === 0) {
      getAccurateLocation();
    } else {
      dispatch(
        filterActions.togglelocation({
          latitude: storedLocation?.latitude,
          longitude: storedLocation?.longitude,
          address: storedLocation?.address,
          place: storedLocation?.place,
          pincode: storedLocation?.pincode,
        })
      );
    }
  }, []);

  // useEffect(() => {
  //   // auto fill of location details
  //   autoLocationHandler();
  // }, [autoLocationHandler]);

  //to get location from browser

  const getCartData = async () => {
    const response = await fetch(`${API_BASE_URL}cart/list/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.sessionToken}`,
      },
    });
    const data = await response.json();

    if (!response.ok) {
      // message.error(data.message);
      throw new Error(data.message);
    }

    dispatch(cartActions.toggleCart({ cartTotal: data.total }));
    // return data.data;
  };

  const getWishlistData = async () => {
    const response = await fetch(`${API_BASE_URL}wishlist/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.sessionToken}`,
      },
    });
    const data = await response.json();

    if (!response.ok) {
      // message.error(data.message);
      throw new Error(data.message);
    }

    console.log("wishlist data", data);
    dispatch(
      wishlistActions.toggleWishlist({
        totalItems: data.total || data.data?.length,
        wishlist: data.data,
      })
    );
    // return data.data;
  };

  useEffect(() => {
    if (user?.sessionToken) {
      getCartData();
      getWishlistData();
    }
  }, [user]);

  // Scroll to top whenever the route changes
  useEffect(() => {
    console.log('latest location', location.pathname)
    if (
  !location.pathname.includes("/search") &&
  location.pathname !== "/blogs"
) {
  window.scrollTo(0, 0);
}

  }, [location.pathname]);

  const getLatLngFromPincode = async (pincode) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?postalcode=676317&country=India&format=json`
    );
    const data = await response.json();

    if (data?.length > 0) {
      const { lat, lon } = data[0];
      return { latitude: parseFloat(lat), longitude: parseFloat(lon) };
    } else {
      return null;
    }
  };

  useEffect(() => {
    getLatLngFromPincode();
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          // Seed Token
          colorPrimary: "#FF6666",
          colorTextPlaceholder: "#A1A1A1",
          // borderRadius: 2,

          // Alias Token
          // colorBgContainer: '#f6ffed',
        },
      }}
    >
      {/* 🔻 Place here so it applies globally */}
      <SessionModalManager data = {homeApis?.settings?.[0]} />
      <QueryClientProvider client={queryClient}>
        <NavigationContext.Provider
          value={{
            isBackNavigation,
            setIsBackNavigation,
            searchPageLoading,
            setSearchPageLoading,
          }}
        >
          {/* Defined seperate routes to avoid coming under Layout component */}
          <Routes>
            <Route
              path={`${APP_PREFIX_PATH}/login`}
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Login />
                </Suspense>
              }
            />
            <Route
              path={`${APP_PREFIX_PATH}/signup`}
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <SignUp />
                </Suspense>
              }
            />
          </Routes>
          <Layout
            isDashboard={
              location.pathname.includes("dashboard") ||
              location.pathname.includes("profile")
            }
            notScrollable={modalIsVisible}
          >
            {/* Define Routes and corresponding components of main pages */}
            <Routes>
              <Route
                path={`${APP_PREFIX_PATH}`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Home />
                  </Suspense>
                }
              />
              {/* <Route
                path={`${APP_PREFIX_PATH}/search`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <SearchPage />
                  </Suspense>
                }
              /> */}
              <Route
                path={`${APP_PREFIX_PATH}/search`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <SearchPageLatest />
                  </Suspense>
                }
              />
               <Route
                path={`${APP_PREFIX_PATH}/listings/:category/:location`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ListingByCategoryAndLocation />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/about-us`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <AboutUsTemplate />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/plan-with-eventoq`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PlanWithUs />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/package-search`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PackageSearch />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/wishlist/*`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <WishList />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/bookings`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <BookingsPage />
                  </Suspense>
                }
              />
              {/* {isLoggedIn && (
               <Route
                path={`${APP_PREFIX_PATH}/enquiries`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <EnquiriesListing />
                  </Suspense>
                }
              />
              )} */}
              <Route
                path={`${APP_PREFIX_PATH}/profile`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProfileSettings />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/contact-us`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ContactUs />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/checkout/:itemId`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Checkout />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/faq`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <FAQ />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/information-policy/:infoSlug`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PolicyDetail />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/payment-success`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PaymentSuccess />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/payment-failure`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PaymentFailure />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/packages/package`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PackageDetail />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/blogs`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <BlogsList />
                  </Suspense>
                }
              />
               <Route
                path={`${APP_PREFIX_PATH}/vendors`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <VendorsList />
                  </Suspense>
                }
              />
               <Route
                path={`${APP_PREFIX_PATH}/vendors/:id`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <VendorDetail />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/blog/:slug`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <BlogDetail />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/cart`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Cart />
                  </Suspense>
                }
              />

              <Route
                path={`${APP_PREFIX_PATH}/:categoryName/:venueId`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <VenueDetail />
                  </Suspense>
                }
              />
              <Route
                path={`${APP_PREFIX_PATH}/:categoryName/:venueId/gallery`}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Gallery />
                  </Suspense>
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <NotFound />
                  </Suspense>
                }
              />

              {/* Redirect Route */}
              {/* {checkForAuthorization && (
            <Route
              path="*"
              element={<Navigate to={`${APP_PREFIX_PATH}`} replace />}
            />
          )} */}
              {/* <Route
              path={`${APP_PREFIX_PATH}/test`}
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <TestData />
                </Suspense>
              }
            /> */}
            </Routes>
          </Layout>
        </NavigationContext.Provider>
      </QueryClientProvider>
    </ConfigProvider>
  );
};

export default App;
